import BuildIcon from "@mui/icons-material/Build";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import { StatusBadge, STATUS_BADGES } from "@/shared/components/StatusBadge";
import { ServerFavicon } from "@/shared/domain/mcp/components/ServerFavicon";
import {
  useAdminMcpServers,
  adminMcpServersQueryKey,
} from "@/shared/domain/mcp/hooks/useAdminMcpServers";
import { useAdminRegisterMcpServer } from "@/shared/domain/mcp/hooks/useAdminRegisterMcpServer";
import { useAdminUpdateMcpServerOrgs } from "@/shared/domain/mcp/hooks/useAdminUpdateMcpServerOrgs";
import { McpServerRecord } from "@/shared/domain/mcp/McpServerRecord";
import { Badge, Button } from "@/shared/foundation/primitives";

import { EditAllowedToolsDialog } from "./EditAllowedToolsDialog";
import styles from "./McpServers.module.css";
import { OrgAutocomplete } from "./OrgAutocomplete";
import {
  McpServerFormData,
  RegisterMcpServerDialog,
} from "./RegisterMcpServerDialog";

export function McpServers() {
  const { data: servers, isLoading } = useAdminMcpServers();
  const registerMutation = useAdminRegisterMcpServer();
  const { mcpRegistryService } = useDomainServices();
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (serverId: string) =>
      mcpRegistryService.adminDeleteServer(serverId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adminMcpServersQueryKey,
      });
    },
  });

  const updateOrgsMutation = useAdminUpdateMcpServerOrgs();

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [formOrgIds, setFormOrgIds] = React.useState<string[]>([]);

  // Edit orgs dialog state
  const [editOrgsServer, setEditOrgsServer] =
    React.useState<McpServerRecord | null>(null);
  const [editOrgIds, setEditOrgIds] = React.useState<string[]>([]);

  // Edit allowed tools dialog state
  const [editToolsServer, setEditToolsServer] =
    React.useState<McpServerRecord | null>(null);

  const openEditOrgsDialog = (server: McpServerRecord) => {
    setEditOrgsServer(server);
    setEditOrgIds([...server.org_ids]);
  };

  const handleEditOrgsSubmit = () => {
    if (!editOrgsServer) {
      return;
    }
    updateOrgsMutation.mutate(
      { serverId: editOrgsServer.server_id, orgIds: editOrgIds },
      {
        onSuccess: () => setEditOrgsServer(null),
      },
    );
  };

  const handleRegister = (data: McpServerFormData) => {
    registerMutation.mutate(
      {
        ...data,
        org_ids: formOrgIds,
      },
      {
        onSuccess: () => {
          setDialogOpen(false);
          setFormOrgIds([]);
        },
      },
    );
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <div>
      <div className={styles.header}>
        <Typography variant="h6">MCP Server Registrations</Typography>
        <Button variant="contained" onClick={() => setDialogOpen(true)}>
          Register Server
        </Button>
      </div>

      <Typography
        variant="body2"
        color="text.secondary"
        className={styles.description}
      >
        Manage MCP server registrations. Servers can be global (available to all
        orgs) or scoped to specific orgs. Each user connects their own OAuth
        token.
      </Typography>

      {registerMutation.isError && (
        <Alert severity="error" className={styles.alert}>
          Failed to register server: {String(registerMutation.error)}
        </Alert>
      )}

      {servers && servers.length > 0 ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Server URL</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Orgs</TableCell>
                <TableCell>Allowed Tools</TableCell>
                <TableCell>OAuth Issuer</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {servers.map((server) => (
                <TableRow key={server.server_id}>
                  <TableCell>
                    <div className={styles.serverNameCell}>
                      <ServerFavicon
                        issuerUrl={server.oauth_issuer_url}
                        displayName={server.display_name}
                      />
                      {server.display_name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      noWrap
                      style={{ maxWidth: 300 }}
                    >
                      {server.server_url}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Badge variant="label">{server.registration_mode}</Badge>
                  </TableCell>
                  <TableCell>
                    {server.org_ids.length > 0 ? (
                      <span className={styles.labels}>
                        {server.org_ids.map((id) => (
                          <Badge key={id} variant="label">
                            {id}
                          </Badge>
                        ))}
                      </span>
                    ) : (
                      <Badge variant="label">Global</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {server.allowed_tools === null ? (
                      <StatusBadge
                        status={STATUS_BADGES.mcpAllowedTools.not_configured}
                      />
                    ) : (
                      (() => {
                        const enabled = Object.values(
                          server.allowed_tools,
                        ).filter(Boolean).length;
                        const total = Object.keys(server.allowed_tools).length;
                        return (
                          <StatusBadge
                            status={
                              enabled === 0
                                ? STATUS_BADGES.mcpAllowedTools.none_enabled
                                : STATUS_BADGES.mcpAllowedTools.enabled
                            }
                            label={`${enabled}/${total} enabled`}
                          />
                        );
                      })()
                    )}
                  </TableCell>
                  <TableCell>{server.oauth_issuer_url}</TableCell>
                  <TableCell>
                    {server.created_by.replace(/^user:/, "")}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit allowed tools">
                      <IconButton
                        size="small"
                        onClick={() => setEditToolsServer(server)}
                      >
                        <BuildIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={() => openEditOrgsDialog(server)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => deleteMutation.mutate(server.server_id)}
                      disabled={deleteMutation.isPending}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Card variant="outlined" className={styles.emptyState}>
          <CardContent>
            <Typography color="text.secondary">
              No MCP servers registered yet.
            </Typography>
          </CardContent>
        </Card>
      )}

      <RegisterMcpServerDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleRegister}
        loading={registerMutation.isPending}
        error={
          registerMutation.isError ? String(registerMutation.error) : undefined
        }
        footer={
          <OrgAutocomplete
            selectedOrgIds={formOrgIds}
            onChange={setFormOrgIds}
          />
        }
      />

      <EditAllowedToolsDialog
        server={editToolsServer}
        allServers={servers ?? []}
        onClose={() => setEditToolsServer(null)}
      />

      {/* Edit orgs dialog */}
      <Dialog
        open={editOrgsServer !== null}
        onClose={() => setEditOrgsServer(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Org Scope &mdash; {editOrgsServer?.display_name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Update which organizations can see this server. Leave empty to make
            it global. Tokens for removed orgs will be deleted.
          </Typography>

          {updateOrgsMutation.isError && (
            <Alert severity="error" className={styles.alert}>
              {String(updateOrgsMutation.error)}
            </Alert>
          )}

          <OrgAutocomplete
            selectedOrgIds={editOrgIds}
            onChange={setEditOrgIds}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditOrgsServer(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditOrgsSubmit}
            disabled={updateOrgsMutation.isPending}
          >
            {updateOrgsMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
